// src/components/CariHesapEkstresi.js

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { db } from '../firebase';
import { doc, collection, query, where, onSnapshot, getDocs, runTransaction, deleteDoc } from 'firebase/firestore';
import { Box, Typography, Paper, Grid, CircularProgress, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider, Link as MuiLink, Stack, Chip, IconButton, Tooltip, Alert, AlertTitle } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import PrintIcon from '@mui/icons-material/Print';
import PersonIcon from '@mui/icons-material/Person';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PlanlanmisOdemeModal from './PlanlanmisOdemeModal';
import { useAuth } from '../context/AuthContext';
// YENİ: Logo import edildi
import logo from '../assets/logo.png';

const formatCurrency = (amount) => (amount ?? 0).toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' });
const formatDate = (timestamp) => timestamp ? timestamp.toDate().toLocaleDateString('tr-TR') : '---';

const CariHesapEkstresi = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [scheduledPayments, setScheduledPayments] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isPlanlamaModalOpen, setIsPlanlamaModalOpen] = useState(false);
  const [paymentToEdit, setPaymentToEdit] = useState(null);
  const [workOrderPayments, setWorkOrderPayments] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    setLoading(true);
    const unsubCustomer = onSnapshot(doc(db, "customers", id), (docSnap) => {
      if (docSnap.exists()) { setCustomer({ id: docSnap.id, ...docSnap.data() }); }
    });
    const unsubVehicles = onSnapshot(query(collection(db, "vehicles"), where("musteriId", "==", id)), (snapshot) => {
      setVehicles(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubWorkOrders = onSnapshot(query(collection(db, "workOrders"), where("musteriId", "==", id)), (snapshot) => {
      setWorkOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubTransactions = onSnapshot(query(collection(db, "transactions"), where("customerId", "==", id)), (snapshot) => {
      setTransactions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubScheduledPayments = onSnapshot(query(collection(db, "scheduledPayments"), where("musteriId", "==", id), where("durum", "==", "Bekleniyor")), (snapshot) => {
      setScheduledPayments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });

    return () => { unsubCustomer(); unsubVehicles(); unsubWorkOrders(); unsubTransactions(); unsubScheduledPayments(); };
  }, [id]);

  // İş emri ödemeleri GERÇEK tarihleriyle listelensin diye payments alt-koleksiyonları okunur.
  // (Eskiden tüm ödemeler tek kalem halinde iş emri tarihine yazılıyordu -> yürüyen bakiye kronolojik olarak yanlıştı.)
  const workOrderKey = workOrders.map(w => `${w.id}:${w.odenenTutar || 0}`).join('|');
  useEffect(() => {
    let iptal = false;
    const yukle = async () => {
      const hepsi = [];
      for (const wo of workOrders) {
        try {
          const snap = await getDocs(collection(db, "workOrders", wo.id, "payments"));
          snap.forEach(d => {
            const p = d.data();
            hepsi.push({
              id: d.id, workOrderId: wo.id, plaka: wo.aracPlaka || '',
              tutar: p.tutar || 0, tarih: p.tarih || wo.isEmriTarihi, aciklama: p.aciklama || 'Ödeme'
            });
          });
        } catch (e) {
          console.error("Ödeme kayıtları okunamadı:", wo.id, e);
        }
      }
      if (!iptal) setWorkOrderPayments(hepsi);
    };
    if (workOrders.length) yukle(); else setWorkOrderPayments([]);
    return () => { iptal = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderKey]);

  // G5 (2026-08-29): "Tahsilat Ekle" ve "Borç Ekle" kaldırıldı — tahsilat YALNIZ iş emri
  // üzerinden girilir; istisnai satış/borç için "Diğer" hizmetli iş emri açılır.
  // Legacy transactions (genel tahsilat/borç) kayıtları ekstrede okunmaya ve A formülünde
  // sayılmaya DEVAM eder; silme (GM) korunur.

  const handleDeleteTransaction = async (tx) => {
    if (window.confirm(`${formatCurrency(tx.amount)} tutarındaki "${tx.description}" kaydını silmek istediğinizden emin misiniz? \n\nBU İŞLEM MÜŞTERİNİN ANA BAKİYESİNİ ETKİLEYECEKTİR VE GERİ ALINAMAZ!`)) {
      const customerRef = doc(db, "customers", id);
      const txRef = doc(db, "transactions", tx.refId);
      try {
        await runTransaction(db, async (transaction) => {
          const customerDoc = await transaction.get(customerRef);
          if (!customerDoc.exists()) throw "Müşteri bulunamadı!";
          const customerData = customerDoc.data();

          let newToplamSatis = customerData.toplamSatis || 0;
          let newToplamOdeme = customerData.toplamOdeme || 0;
          let newToplamBakiye = customerData.toplamBakiye || 0;

          if (tx.type === 'borc') {
            newToplamSatis -= tx.amount;
            newToplamBakiye -= tx.amount;
          } else {
            newToplamOdeme -= tx.amount;
            newToplamBakiye += tx.amount;
          }

          transaction.update(customerRef, {
            toplamSatis: newToplamSatis,
            toplamOdeme: newToplamOdeme,
            toplamBakiye: newToplamBakiye
          });

          transaction.delete(txRef);
        });
        alert("Finansal kayıt başarıyla silindi ve bakiye güncellendi.");
      } catch (e) {
        console.error("Kayıt silinirken hata:", e);
        alert("Bir hata oluştu: " + e.message);
      }
    }
  };

  const handleDeleteScheduledPayment = async (paymentId) => {
    if (window.confirm("Planlanmış tahsilat kaydını silmek istediğinizden emin misiniz?")) {
      try {
        await deleteDoc(doc(db, "scheduledPayments", paymentId));
        alert("Planlanmış tahsilat silindi.");
      } catch (e) {
        console.error("Vadeli silinirken hata:", e);
        alert("Kayıt silinirken bir hata oluştu.");
      }
    }
  };

  const handleOpenEditPayment = (payment) => {
    setPaymentToEdit(payment);
    setIsPlanlamaModalOpen(true);
  };

  const handleClosePlanlamaModal = () => {
    setIsPlanlamaModalOpen(false);
    setPaymentToEdit(null);
  };

  const allMovements = useMemo(() => {
    const odemeliIsEmirleri = new Set(workOrderPayments.map(p => p.workOrderId));

    const movements = [
      ...workOrders.flatMap(order => {
        const orderMovements = [
          { type: 'borc', date: order.isEmriTarihi, description: `İş Emri #${order.aracPlaka}`, amount: order.toplamTutar, refId: order.id, isWorkOrder: true }
        ];
        // Ödeme kaydı olmayan eski iş emirleri (peşinatı payments'a yazılmamış) için tek kalem geri-uyum satırı
        if (order.odenenTutar > 0 && !odemeliIsEmirleri.has(order.id)) {
          orderMovements.push(
            { type: 'alacak', date: order.isEmriTarihi, description: `İş Emri #${order.aracPlaka} (kayıtsız peşinat)`, amount: order.odenenTutar, refId: order.id, isWorkOrder: true }
          );
        }
        return orderMovements;
      }),
      ...workOrderPayments.map(p => ({
        type: 'alacak',
        date: p.tarih,
        description: `İş Emri #${p.plaka} — ${p.aciklama}`,
        amount: p.tutar,
        refId: p.workOrderId,
        isWorkOrder: true
      })),
      // İş emrine bağlı legacy tahsilat hareketleri ATLANIR: aynı ödeme workOrders.odenenTutar
      // üzerinden zaten sayılıyor; ikisini birden eklemek çift sayım üretir (v1 formülünün hatasıydı).
      ...transactions.filter(t => !t.workOrderId).map(t => ({
        type: t.type === 'borc' ? 'borc' : 'alacak',
        date: t.timestamp,
        description: t.description,
        amount: t.amount,
        refId: t.id,
        isWorkOrder: false,
        paymentMethod: t.paymentMethod
      }))
    ];
    const validMovements = movements.filter(m => m.amount > 0);
    validMovements.sort((a, b) => (a.date?.toMillis?.() || 0) - (b.date?.toMillis?.() || 0));
    let balance = 0;
    return validMovements.map(m => {
      balance += (m.type === 'borc' ? m.amount : -m.amount);
      return { ...m, balance };
    });
  }, [workOrders, transactions, workOrderPayments]);

  const finalBalance = customer?.toplamBakiye || 0;
  const hesaplananBakiye = allMovements.length ? allMovements[allMovements.length - 1].balance : 0;
  const mutabakatFarki = Number((finalBalance - hesaplananBakiye).toFixed(2));
  const mutabakatsizlikVar = Math.abs(mutabakatFarki) > 0.01;

  // Cari kartını hareketlerden yeniden hesaplayıp yazar (v1'de vardı, v2 kopyasında silinmişti).
  // v1 formülü iş emrine bağlı tahsilatı ÇİFT sayıyordu; burada tekilleştirilmiş hali kullanılır.
  const handleSyncBalance = async () => {
    if (!customer) return;
    const beklenenSatis = workOrders.reduce((s, w) => s + (w.toplamTutar || 0), 0)
      + transactions.filter(t => t.type === 'borc').reduce((s, t) => s + (t.amount || 0), 0);
    const beklenenOdeme = workOrders.reduce((s, w) => s + (w.odenenTutar || 0), 0)
      + transactions.filter(t => t.type === 'tahsilat' && !t.workOrderId).reduce((s, t) => s + (t.amount || 0), 0);
    const beklenenBakiye = beklenenSatis - beklenenOdeme;

    const onay = window.confirm(
      `${customer.adSoyad} müşterisinin cari kartı iş emirleri ve hareketlerden YENİDEN HESAPLANACAK.\n\n` +
      `Toplam Satış : ${formatCurrency(customer.toplamSatis)}  →  ${formatCurrency(beklenenSatis)}\n` +
      `Toplam Tahsilat: ${formatCurrency(customer.toplamOdeme)}  →  ${formatCurrency(beklenenOdeme)}\n` +
      `Bakiye        : ${formatCurrency(finalBalance)}  →  ${formatCurrency(beklenenBakiye)}\n\n` +
      `Önceki değerler denetim izi olarak dokümana yazılır. Devam edilsin mi?`
    );
    if (!onay) return;

    setIsSyncing(true);
    try {
      const customerRef = doc(db, "customers", customer.id);
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(customerRef);
        if (!snap.exists()) throw new Error("Müşteri bulunamadı!");
        const eski = snap.data();
        transaction.update(customerRef, {
          toplamSatis: beklenenSatis,
          toplamOdeme: beklenenOdeme,
          toplamBakiye: beklenenBakiye,
          bakiyeOnarimi: {
            tarih: new Date(),
            oncekiSatis: eski.toplamSatis || 0,
            oncekiOdeme: eski.toplamOdeme || 0,
            oncekiBakiye: eski.toplamBakiye || 0,
            kaynak: 'cari-ekstre-bakiye-senkronize'
          }
        });
      });
      alert("Cari bakiye senkronize edildi.");
    } catch (e) {
      console.error("Bakiye senkronize edilemedi:", e);
      alert(`Bakiye senkronize edilemedi: ${e?.message || e}`);
    } finally {
      setIsSyncing(false);
    }
  };

  if (loading || !currentUser) { return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />; }
  if (!customer) { return <Typography variant="h5" align="center" sx={{ mt: 4 }}>Müşteri Bulunamadı.</Typography>; }

  return (
    <>
      <PlanlanmisOdemeModal
        isOpen={isPlanlamaModalOpen}
        onClose={handleClosePlanlamaModal}
        customer={customer}
        finalBalance={finalBalance}
        paymentToEdit={paymentToEdit}
      />

      <Box className="printable-area">
        {/* Buton Alanı */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }} className="no-print">
          <Typography variant="h4" component="h1">Hesap Ekstresi</Typography>
          <Box>
            <Button component={RouterLink} to={`/musteri/${customer.id}`} variant="outlined" startIcon={<PersonIcon />} sx={{ mr: 1 }}>Müşteri Detayına Git</Button>
            <Button variant="outlined" startIcon={<PlaylistAddCheckIcon />} sx={{ mr: 1 }} onClick={() => setIsPlanlamaModalOpen(true)}>Ödeme Sözü Ekle</Button>
            <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>Yazdır</Button>
          </Box>
        </Box>

        {/* Sessiz finansal sapmayı görünür kıl: hareketlerin yürüyen bakiyesi ile cari kartı çelişiyorsa bağır. */}
        {mutabakatsizlikVar && (
          <Alert
            severity="error"
            icon={<SyncProblemIcon />}
            sx={{ mb: 3 }}
            action={currentUser.role === 'Genel Müdür' ? (
              <Button color="inherit" size="small" onClick={handleSyncBalance} disabled={isSyncing}>
                {isSyncing ? 'Senkronize ediliyor…' : 'Bakiye Senkronize'}
              </Button>
            ) : null}
            className="no-print"
          >
            <AlertTitle>Mutabakatsızlık: {formatCurrency(Math.abs(mutabakatFarki))}</AlertTitle>
            Hesap hareketlerinden hesaplanan bakiye <b>{formatCurrency(hesaplananBakiye)}</b>, cari kartındaki bakiye ise <b>{formatCurrency(finalBalance)}</b>.
            {currentUser.role === 'Genel Müdür'
              ? ' Kartı hareketlerden yeniden hesaplamak için "Bakiye Senkronize"yi kullanın.'
              : ' Düzeltme için Genel Müdür ile iletişime geçin.'}
          </Alert>
        )}

        <Alert severity="info" sx={{ mb: 3 }} className="no-print">
          Tahsilatlar <b>yalnız iş emri üzerinden</b> girilir: ödemeyi İş Emri Detay ekranındaki
          "Ödeme Ekle" ile kaydedin. İstisnai satış/borç için "Diğer" hizmetli yeni iş emri açın.
          Geçmişte girilmiş genel tahsilat/borç kayıtları bu ekstrede görünmeye devam eder.
        </Alert>

        {/* YENİ: Yazdırma Logosu (Sadece print modunda görünür) */}
        <Box sx={{ display: 'none', '@media print': { display: 'flex', justifyContent: 'center', mb: 4 } }}>
          <img src={logo} alt="Logo" style={{ maxHeight: '80px', objectFit: 'contain' }} />
        </Box>

        {/* Kart Başlık Alanı */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Sol Kart: Müşteri ve Araç Bilgileri */}
          <Grid item xs={12} md={5} lg={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>{customer.adSoyad}</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>{customer.telefon}</Typography>
              <Divider />
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Kayıtlı Araçlar ({vehicles.length})</Typography>
              <Stack spacing={1}>
                {vehicles.length > 0 ? (
                  vehicles.map(vehicle => (
                    <MuiLink component={RouterLink} to={`/arac/${vehicle.id}`} key={vehicle.id} sx={{ textDecoration: 'none', color: 'inherit' }}>
                      <Chip
                        icon={<DirectionsCarIcon />}
                        label={`${vehicle.plaka} (${vehicle.marka} ${vehicle.model})`}
                        variant="outlined"
                        clickable
                        sx={{ justifyContent: 'flex-start', width: '100%', py: 2 }}
                      />
                    </MuiLink>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">Bu müşteriye ait kayıtlı araç bulunamadı.</Typography>
                )}
              </Stack>
            </Paper>
          </Grid>

          {/* Sağ Kart: Finansal Özet */}
          <Grid item xs={12} md={7} lg={8}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Finansal Özet</Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Toplam Satış</Typography>
                  <Typography variant="h5" fontWeight="bold">{formatCurrency(customer.toplamSatis)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Toplam Tahsilat</Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">{formatCurrency(customer.toplamOdeme)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Güncel Bakiye</Typography>
                  <Typography variant="h4" fontWeight="bold" color={finalBalance > 0 ? 'error.main' : 'inherit'}>
                    {formatCurrency(finalBalance)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        {/* Ana İçerik (Ekstre Tablosu) */}
        <Paper sx={{ p: { xs: 1, sm: 3 } }}>
          <Typography variant="h6" sx={{ my: 2 }}>Hesap Hareketleri</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tarih</TableCell>
                  <TableCell>Açıklama</TableCell>
                  <TableCell align="right">Borç</TableCell>
                  <TableCell align="right">Alacak</TableCell>
                  <TableCell align="right">Bakiye</TableCell>
                  {currentUser.role === 'Genel Müdür' && (
                    <TableCell align="center" className="no-print">İşlemler</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {allMovements.map((m, index) => (
                  <TableRow key={index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>{formatDate(m.date)}</TableCell>
                    <TableCell>
                      {m.isWorkOrder ? (
                        <MuiLink component={RouterLink} to={`/is-emri/${m.refId}`} sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', '&:hover': { textDecoration: 'underline' } }}>
                          {m.description} <LaunchIcon fontSize="small" sx={{ ml: 0.5 }} className="no-print" />
                        </MuiLink>
                      ) : (m.description)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main' }}>{m.type === 'borc' ? formatCurrency(m.amount) : '-'}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>{m.type === 'alacak' ? formatCurrency(m.amount) : '-'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(m.balance)}</TableCell>

                    {currentUser.role === 'Genel Müdür' && (
                      <TableCell align="center" className="no-print">
                        {!m.isWorkOrder ? (
                          <IconButton size="small" color="error" onClick={() => handleDeleteTransaction(m)} title="Finansal Kaydı Sil">
                            <DeleteIcon />
                          </IconButton>
                        ) : (
                          <Tooltip title="İş emirleri buradan silinemez. Lütfen İş Emirleri sayfasını kullanın.">
                            <span>
                              <IconButton size="small" disabled><DeleteIcon /></IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Vadeli Tahsilatlar Tablosu */}
          {scheduledPayments.length > 0 && (
            <Box sx={{ mt: 4 }} className="no-print">
              <Typography variant="h6" sx={{ my: 2 }}>Beklenen Tahsilatlar (Vadeli)</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vade Tarihi</TableCell>
                      <TableCell>Açıklama</TableCell>
                      <TableCell align="right">Tutar</TableCell>
                      {currentUser.role === 'Genel Müdür' && (
                        <TableCell align="center">İşlemler</TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scheduledPayments.map(p => (
                      <TableRow key={p.id}>
                        <TableCell>{formatDate(p.vadeTarihi)}</TableCell>
                        <TableCell>{p.aciklama || p.odemeYontemi}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(p.tutar)}</TableCell>
                        {currentUser.role === 'Genel Müdür' && (
                          <TableCell align="center">
                            <IconButton size="small" color="primary" onClick={() => handleOpenEditPayment(p)} title="Kaydı Düzenle">
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteScheduledPayment(p.id)} title="Kaydı Sil">
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default CariHesapEkstresi;
